import { useMemo, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { useReactTable, getCoreRowModel, type ColumnDef } from '@tanstack/react-table'

import { Button } from '@atoms'
import { SelectField } from '@components/molecules/SelectField'
import SearchField from '@components/molecules/SearchField'
import LoadingWrapper from '@components/molecules/LoadingWrapper'
import { MultiSelectField } from '@components/molecules/MultiSelectField'
import { SegmentedControl, type SegmentedOption } from '@components/atoms/SegmentedControl'
import { ColumnVisibilityDropdown } from '@components/molecules/ColumnVisibilityDropdown'
import { DataTableRoot, DataTableContent, DataTableFooter } from '@components/molecules/DataTable'
import { TooltipProvider } from '@components/atoms/Tooltip'
import { TruncationTooltip } from '@components/molecules/TruncationTooltip'
import { BlastStatusBadge, blastStatusLabel } from '@components/atoms/BlastStatusBadge'
import { BlastRowActions } from '@components/molecules/BlastRowActions'
import Pagination from '@molecules/Pagination'
import { PageLayout } from '@components/layouts/PageLayout'
import { PageHeaderNav, type PageTabs } from '@molecules/PageHeaderNav'
import Icon from '@components/utils/Icon'
import cn from '@components/utils/cn'
import { useColumnVisibility } from '@hooks/useColumnVisibility'
import { useLocalStorage } from '@hooks/useLocalStorage'
import { scopedTranslation } from '@utils/I18n'
import {
  GroupPageHeader,
  type GroupPageHeaderProps,
} from '@frontend/contexts/supporters-management/features/group-page/GroupPageHeader'

import type { Blast, BlastChannel, BlastStatus } from '../types'
import type { BlastPermissions } from '../utils/blastPermissions'
import { groupUrl } from '../utils/routes'
import { BlastActiveFilters } from '../features/blasts-index/BlastActiveFilters'
import { BlastCard } from '../features/blasts-index/BlastCard'
import { BlastsEmptyState } from '../features/blasts-index/BlastsEmptyState'
import { buildBlastAction } from '../features/blasts-index/useBlastActions'
import {
  audienceLabel,
  formatDate,
  formatDateTime,
  statusDetail,
  unavailableReason,
} from '../features/blasts-index/blastPresenters'

const t = scopedTranslation('comms_management.blasts_index')
const tGroupFilter = scopedTranslation('shared.group_filter')

type Layout = 'table' | 'cards'

const RELOAD_KEYS = ['blasts', 'filters', 'sort', 'page', 'totalCount', 'totalPages']

type BlastsIndexProps = {
  channel: BlastChannel
  newBlastPath: string
  blasts: Blast[]
  groups: { id: string; name: string; awaitingCount: number }[]
  statuses: BlastStatus[]
  filters: { keyword: string | null; groupIds: string[]; statuses: BlastStatus[] }
  sort: { column: string; direction: 'asc' | 'desc' }
  sortableColumns: string[]
  permissions: BlastPermissions
  currentUserId: string
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
  groupHeader?: GroupPageHeaderProps
  pageTabs?: PageTabs
}

export default function BlastsIndex({
  channel,
  newBlastPath,
  blasts,
  groups,
  statuses,
  filters,
  sort,
  sortableColumns,
  permissions,
  currentUserId,
  page,
  pageSize,
  totalCount,
  totalPages,
  groupHeader,
  pageTabs,
}: BlastsIndexProps) {
  const [layout, setLayout] = useLocalStorage<Layout>('viewMode:blastsIndex', 'cards')
  const [searching, setSearching] = useState(false)
  const [searchKey, setSearchKey] = useState(0)
  const [deletedIds, setDeletedIds] = useState<string[]>([])
  const { columnVisibility, setColumnVisibility } = useColumnVisibility('blastsIndex')

  const showGroups = groups.length > 1
  const compactToolbar = layout !== 'table'
  const hasFilters = Boolean(filters.keyword) || filters.groupIds.length > 0 || filters.statuses.length > 0
  const visibleBlasts = useMemo(() => blasts.filter((b) => !deletedIds.includes(b.id)), [blasts, deletedIds])

  const reload = (query: Record<string, string | string[] | number | null>) =>
    router.get(window.location.pathname, query, {
      only: RELOAD_KEYS,
      preserveState: true,
      replace: true,
      onStart: () => setSearching(true),
      onFinish: () => setSearching(false),
    })

  const applyFilters = (next: Partial<{ keyword: string; groupIds: string[]; statuses: BlastStatus[] }>) =>
    reload({
      keyword: next.keyword ?? filters.keyword ?? '',
      filter_group_ids: next.groupIds ?? filters.groupIds,
      statuses: next.statuses ?? filters.statuses,
      sort: sort.column,
      direction: sort.direction,
      page: 1,
    })

  const sortBy = (column: string, direction: 'asc' | 'desc') =>
    reload({
      keyword: filters.keyword ?? '',
      filter_group_ids: filters.groupIds,
      statuses: filters.statuses,
      sort: column,
      direction,
      page: 1,
    })

  const actionFor = (blast: Blast) =>
    buildBlastAction({
      blast,
      channel,
      permissions,
      currentUserId,
      reasonFor: unavailableReason,
      onDeleted: () => setDeletedIds((ids) => [...ids, blast.id]),
    })

  const header = (column: string, label: string) =>
    sortableColumns.includes(column) ? (
      <button
        onClick={() => sortBy(column, sort.column === column && sort.direction === 'desc' ? 'asc' : 'desc')}
        aria-label={t('sort_by', { column: label })}
        className="tw:flex tw:items-center tw:gap-1 tw:font-bold tw:hover:text-gray-900"
      >
        {label}
        {sort.column === column && (
          <Icon
            type={sort.direction === 'asc' ? 'sort-ascending' : 'sort-descending'}
            width={14}
            height={14}
            aria-hidden
          />
        )}
      </button>
    ) : (
      <span className="tw:font-bold">{label}</span>
    )

  const columns = useMemo<ColumnDef<Blast>[]>(() => {
    const cols: ColumnDef<Blast>[] = [
      {
        id: 'Subject',
        accessorKey: 'title',
        header: () => header('title', t(`columns.title.${channel}`)),
        cell: ({ row }) => (
          <a
            href={actionFor(row.original).blastUrl}
            className={cn(
              'tw:block tw:w-fit tw:max-w-full tw:text-sm tw:font-semibold tw:text-gray-900 tw:hover:text-primary-700 tw:sm:max-w-md',
              channel === 'sms' ? 'tw:line-clamp-2' : 'tw:truncate'
            )}
          >
            {row.original.title}
          </a>
        ),
        minSize: 260,
        meta: { allowWrap: true },
      },
    ]

    if (showGroups) {
      cols.push({
        id: 'Group',
        accessorFn: (blast) => blast.actionGroup?.name ?? '',
        header: () => header('group', t('columns.group')),
        cell: ({ row }) =>
          row.original.actionGroup && (
            <TruncationTooltip content={row.original.actionGroup.name}>
              <span className="tw:block tw:max-w-40 tw:truncate">
                <Link
                  href={groupUrl(row.original.actionGroup.id)}
                  className="tw:border-b tw:border-transparent tw:font-medium tw:text-primary-700 tw:hover:border-current tw:hover:text-primary-900"
                >
                  {row.original.actionGroup.name}
                </Link>
              </span>
            </TruncationTooltip>
          ),
        size: 150,
      })
    }

    cols.push(
      {
        id: 'Status',
        accessorKey: 'status',
        header: () => header('status', t('columns.status')),
        cell: ({ row }) => <BlastStatusBadge status={row.original.status as BlastStatus} />,
        size: 150,
      },
      {
        id: 'Audience',
        accessorFn: (blast) => audienceLabel(blast.audience),
        header: () => <span className="tw:font-bold">{t('columns.audience')}</span>,
        cell: ({ row }) => (
          <span className="tw:block tw:truncate tw:text-gray-700">{audienceLabel(row.original.audience)}</span>
        ),
        size: 220,
      },
      {
        id: 'Recipients',
        accessorKey: 'recipientCount',
        header: () => <span className="tw:font-bold">{t('columns.recipients')}</span>,
        cell: ({ row }) => <span className="tw:tabular-nums">{row.original.recipientCount.toLocaleString()}</span>,
        size: 110,
        meta: { centerContent: true },
      },
      {
        id: 'Sent or scheduled',
        accessorFn: (blast) => blast.sentAt ?? blast.scheduledFor ?? '',
        header: () => header('sent_or_scheduled', t('columns.sent_or_scheduled')),
        cell: ({ row }) => <SentOrScheduledCell blast={row.original} />,
        size: 170,
      },
      {
        id: 'Created',
        accessorKey: 'createdAt',
        header: () => header('created', t('columns.created')),
        cell: ({ row }) => (
          <span className="tw:whitespace-nowrap tw:text-gray-700">{formatDate(row.original.createdAt)}</span>
        ),
        size: 120,
      },
      {
        id: 'Actions',
        header: () => <span className="tw:sr-only">{t('columns.actions')}</span>,
        cell: ({ row }) => <BlastRowActions action={actionFor(row.original)} compact />,
        enableSorting: false,
        size: 190,
        meta: { stickyRight: true },
      }
    )

    return cols
  }, [channel, showGroups, sort.column, sort.direction, sortableColumns, permissions, currentUserId])

  const table = useReactTable({
    data: visibleBlasts,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: { columnVisibility },
  })

  const layoutOptions: SegmentedOption<Layout>[] = [
    { value: 'cards', label: t('layout.cards'), leadingIcon: 'copy-06' },
    { value: 'table', label: t('layout.table'), leadingIcon: 'table' },
  ]

  const sortOptions = [
    { value: 'created:desc', label: t('sort.newest') },
    { value: 'created:asc', label: t('sort.oldest') },
    { value: 'scheduled_for:asc', label: t('sort.sending_soonest') },
    { value: 'sent_at:desc', label: t('sort.recently_sent') },
    { value: `title:asc`, label: t(`sort.title_asc.${channel}`) },
    ...(showGroups ? [{ value: 'group:asc', label: t('sort.group_asc') }] : []),
  ]
  const currentSort = `${sort.column}:${sort.direction}`

  return (
    <TooltipProvider>
      <PageLayout.Root>
        {groupHeader ? (
          <GroupPageHeader {...groupHeader} linkComponent={Link} />
        ) : (
          <PageLayout.Header
            heading={pageTabs ? pageTabs.label : t(`heading.${channel}`)}
            icon={pageTabs ? 'send' : channel === 'email' ? 'mail-01' : 'message-chat-square'}
            subtitle={showGroups || pageTabs ? undefined : groups[0]?.name}
            nav={pageTabs && <PageHeaderNav ariaLabel={pageTabs.label} items={pageTabs.items} linkComponent={Link} />}
            actions={
              <Button rank="primary" leadingIcon="plus" onClick={() => router.visit(newBlastPath)}>
                {t(`new_blast.${channel}`)}
              </Button>
            }
          />
        )}

        <PageLayout.Body>
          <div className="tw:flex tw:flex-wrap tw:items-end tw:gap-3 tw:pb-6">
            <div
              className={cn(
                'tw:w-full tw:flex-1 tw:sm:w-auto',
                compactToolbar ? 'tw:min-w-36 tw:sm:basis-48' : 'tw:min-w-48 tw:sm:basis-64'
              )}
            >
              <SearchField
                key={searchKey}
                fieldName="keyword"
                displayName={t(`search.${channel}`)}
                defaultValue={filters.keyword ?? ''}
                onChange={(event) => applyFilters({ keyword: event.target.value })}
              />
            </div>

            <div className={cn('tw:w-full tw:shrink-0 tw:sm:w-40', compactToolbar && 'tw:xl:w-36')}>
              <MultiSelectField
                label={t('filters.status')}
                placeholder={t('filters.all_statuses')}
                options={statuses.map((status) => ({ value: status, label: blastStatusLabel(status) }))}
                value={filters.statuses}
                onValueChange={(next) => applyFilters({ statuses: next as BlastStatus[] })}
                searchPlaceholder={t('filters.status_search')}
                noResultsText={t('filters.status_none')}
                showSelectedPills={false}
              />
            </div>

            {showGroups && (
              <div className={cn('tw:w-full tw:shrink-0 tw:sm:w-40', compactToolbar && 'tw:xl:w-36')}>
                <MultiSelectField
                  label={tGroupFilter('group')}
                  placeholder={tGroupFilter('all_groups')}
                  options={groups.map((group) => ({
                    value: group.id,
                    label: group.awaitingCount > 0 ? `${group.name} (${group.awaitingCount})` : group.name,
                  }))}
                  value={filters.groupIds}
                  onValueChange={(next) => applyFilters({ groupIds: next })}
                  searchPlaceholder={tGroupFilter('search')}
                  noResultsText={tGroupFilter('none')}
                  showSelectedPills={false}
                />
              </div>
            )}

            <div className={cn('tw:w-full tw:shrink-0 tw:sm:w-52', compactToolbar && 'tw:xl:w-48')}>
              <SelectField
                name="sort"
                label={t('sort.label')}
                options={sortOptions}
                value={currentSort}
                onValueChange={(value) => {
                  const [column, direction] = value.split(':')
                  sortBy(column, direction as 'asc' | 'desc')
                }}
              />
            </div>

            <div className="tw:flex tw:shrink-0 tw:flex-nowrap tw:items-center tw:gap-3 tw:sm:ml-auto">
              {layout === 'table' && (
                <div className="tw:hidden tw:md:block">
                  <ColumnVisibilityDropdown table={table} nonHideableColumns={['Subject', 'Actions']} />
                </div>
              )}
              <SegmentedControl<Layout>
                options={layoutOptions}
                value={layout}
                onChange={setLayout}
                ariaLabel={t('layout.aria')}
              />
            </div>
          </div>

          <div className="tw:w-full tw:overflow-hidden tw:rounded-xl tw:border tw:border-gray-200 tw:shadow-xs">
            <BlastActiveFilters
              keyword={filters.keyword}
              groups={groups.filter((group) => filters.groupIds.includes(group.id))}
              statuses={filters.statuses}
              onRemoveStatus={(status) => applyFilters({ statuses: filters.statuses.filter((s) => s !== status) })}
              onRemoveGroup={(id) => applyFilters({ groupIds: filters.groupIds.filter((g) => g !== id) })}
              onRemoveKeyword={() => {
                setSearchKey((key) => key + 1)
                applyFilters({ keyword: '' })
              }}
              onClear={() => {
                setSearchKey((key) => key + 1)
                reload({ sort: sort.column, direction: sort.direction, page: 1 })
              }}
            />

            <LoadingWrapper loading={searching}>
              {visibleBlasts.length === 0 ? (
                <BlastsEmptyState
                  className="tw:rounded-none tw:border-0 tw:bg-white"
                  channel={channel}
                  hasFilters={hasFilters}
                  newBlastPath={newBlastPath}
                />
              ) : layout === 'cards' ? (
                <>
                  <ul className="tw:flex tw:flex-col tw:gap-4 tw:bg-gray-cool-50 tw:p-4 tw:sm:p-6">
                    {visibleBlasts.map((blast) => (
                      <BlastCard
                        key={blast.id}
                        blast={blast}
                        action={actionFor(blast)}
                        audienceLabel={audienceLabel(blast.audience)}
                        statusDetail={statusDetail(blast)}
                      />
                    ))}
                  </ul>
                  <Footer totalCount={totalCount} pageSize={pageSize} page={page} channel={channel} />
                </>
              ) : (
                <DataTableRoot table={table}>
                  <DataTableContent
                    caption={t(`heading.${channel}`)}
                    className="tw:rounded-none tw:border-x-0 tw:border-t-0 tw:overflow-x-auto"
                  />
                  <Footer totalCount={totalCount} pageSize={pageSize} page={page} channel={channel} />
                </DataTableRoot>
              )}
            </LoadingWrapper>
          </div>

          <div className="tw:py-6">{totalCount > 0 && <Pagination totalPages={totalPages} only={RELOAD_KEYS} />}</div>
        </PageLayout.Body>
      </PageLayout.Root>
    </TooltipProvider>
  )
}

function Footer({
  totalCount,
  pageSize,
  page,
  channel,
}: {
  totalCount: number
  pageSize: number
  page: number
  channel: BlastChannel
}) {
  return (
    <div className="tw:border-t tw:border-gray-200 tw:bg-gray-cool-50">
      <DataTableFooter
        totalCount={totalCount}
        pageSize={pageSize}
        currentPage={page}
        label={t(`footer_label.${channel}`)}
      />
    </div>
  )
}

function SentOrScheduledCell({ blast }: { blast: Blast }) {
  if (blast.status === 'approved') {
    return <span className="tw:whitespace-nowrap tw:text-gray-true-500">{t('sending_now')}</span>
  }
  if (blast.sentAt) {
    return <span className="tw:whitespace-nowrap tw:text-gray-700">{formatDate(blast.sentAt)}</span>
  }
  if (blast.scheduledFor) {
    return <span className="tw:whitespace-nowrap tw:text-gray-700">{formatDateTime(blast.scheduledFor)}</span>
  }
  return null
}
